import { DocumentVersionStatus, type ValidityState } from '@qualicloud/contracts';
import {
  Badge,
  IconAlertOctagon,
  IconAlertTriangle,
  IconCheckCircle,
  IconMinusCircle,
  cn,
} from '@/design-system';
import { formatDateOnly } from '@/lib/format/datetime';
import { presentValidity, type ValidityPresentation } from '@/lib/validity-presentation';
import { DOCUMENT_STATUS_LABEL } from '@/lib/i18n';

const ICONS = {
  expired: IconAlertOctagon,
  expiring: IconAlertTriangle,
  valid: IconCheckCircle,
  none: IconMinusCircle,
} as const;

export interface ValiditySemaphoreProps {
  readonly state: ValidityState;
  readonly daysUntilExpiry: number | null;
  readonly validUntil: string | null;
  /** `cell` na tabela; `chip` compacto; `block` no cabeçalho do detalhe. */
  readonly variant?: 'cell' | 'chip' | 'block';
  readonly className?: string;
}

/**
 * Semáforo de validade da Lista Mestra.
 *
 * PARIDADE COM O LEGADO: as cores de fundo preservam a identidade cromática de
 * `ged_list.twig:220-228` (vermelho vencido, amarelo até 15 dias, verde válido,
 * cinza sem validade), e os limiares vêm inteiros de `@qualicloud/domain` —
 * este componente não decide nada, só desenha.
 *
 * CORREÇÃO DE ACESSIBILIDADE: o legado transmitia a situação SÓ pela cor de
 * fundo da célula, o que é falha de WCAG 1.4.1 e some por completo em impressão
 * monocromática — justamente o cenário de auditoria. Aqui cada estado carrega
 * três sinais independentes: cor, ÍCONE de forma distinta (octógono, triângulo,
 * círculo, traço) e TEXTO. O texto completo para leitor de tela ("vencido há 12
 * dias, venceu em 02/03/2026") vai num `qc-sr-only`, porque a versão visual é
 * necessariamente abreviada numa célula de tabela.
 */
export function ValiditySemaphore({
  state,
  daysUntilExpiry,
  validUntil,
  variant = 'cell',
  className,
}: ValiditySemaphoreProps) {
  const presentation = presentValidity({ state, daysUntilExpiry, validUntil });
  const Icon = ICONS[presentation.iconName];

  if (variant === 'chip') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
          presentation.className,
          className,
        )}
      >
        <Icon size={13} aria-hidden="true" />
        <span aria-hidden="true">{presentation.label}</span>
        <span className="qc-sr-only">{presentation.srText}</span>
      </span>
    );
  }

  if (variant === 'block') {
    return (
      <div className={cn('rounded-md px-3 py-2', presentation.className, className)}>
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <Icon size={16} aria-hidden="true" />
          <span aria-hidden="true">{presentation.label}</span>
          <span className="qc-sr-only">{presentation.srText}</span>
        </p>
        <p aria-hidden="true" className="mt-0.5 text-xs qc-tabular">
          {describeDeadline(presentation, validUntil)}
        </p>
      </div>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex w-full items-center gap-1.5 rounded px-2 py-1 text-sm',
        presentation.className,
        className,
      )}
    >
      <Icon size={14} aria-hidden="true" />
      <span aria-hidden="true" className="qc-tabular">
        {validUntil ? formatDateOnly(validUntil) : 'Sem validade'}
      </span>
      {/* O texto acessível carrega TUDO: situação, prazo e data. */}
      <span className="qc-sr-only">{presentation.srText}</span>
    </span>
  );
}

/** Coluna "dias restantes" — número + sinal textual, nunca só a cor. */
export function DaysRemaining({
  days,
  state,
}: {
  readonly days: number | null;
  readonly state: ValidityState;
}) {
  if (days === null) {
    return (
      <span className="text-sm text-content-subtle">
        <span aria-hidden="true">—</span>
        <span className="qc-sr-only">Sem prazo de validade</span>
      </span>
    );
  }

  const presentation = presentValidity({ state, daysUntilExpiry: days, validUntil: null });

  return (
    <span
      className={cn(
        'text-sm font-medium qc-tabular',
        days < 0 ? 'text-danger-content' : days <= 15 ? 'text-warning-content' : 'text-content',
      )}
    >
      <span aria-hidden="true">
        {days < 0 ? `−${Math.abs(days)}` : days}
      </span>
      <span className="qc-sr-only">{presentation.srText}</span>
    </span>
  );
}

/**
 * Situação da versão.
 *
 * Cada status tem tom próprio, mas o RÓTULO textual é sempre exibido — o tom é
 * reforço, não veículo. `srPrefix` faz o leitor de tela anunciar "Situação:
 * Aprovado" em vez de um "Aprovado" solto no meio da linha da tabela.
 */
export function DocumentStatusBadge({
  status,
  className,
}: {
  readonly status: DocumentVersionStatus;
  readonly className?: string;
}) {
  const tone = STATUS_TONE[status];
  return (
    <Badge tone={tone} srPrefix="Situação:" className={className}>
      {DOCUMENT_STATUS_LABEL[status]}
    </Badge>
  );
}

const STATUS_TONE: Record<
  DocumentVersionStatus,
  'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'outline'
> = {
  [DocumentVersionStatus.DRAFT]: 'outline',
  [DocumentVersionStatus.IN_REVIEW]: 'warning',
  [DocumentVersionStatus.APPROVED]: 'success',
  [DocumentVersionStatus.REJECTED]: 'danger',
  [DocumentVersionStatus.EXPIRED]: 'danger',
  [DocumentVersionStatus.SUPERSEDED]: 'neutral',
  [DocumentVersionStatus.OBSOLETE]: 'neutral',
};

function describeDeadline(
  presentation: ValidityPresentation,
  validUntil: string | null,
): string {
  if (presentation.daysRemaining === null) return 'Este documento não possui prazo de validade.';
  const date = validUntil ? formatDateOnly(validUntil) : null;
  const days = presentation.daysRemaining;

  if (days < 0) {
    const overdue = Math.abs(days);
    return `Venceu há ${overdue} ${overdue === 1 ? 'dia' : 'dias'}${date ? ` — ${date}` : ''}`;
  }
  if (days === 0) return `Vence hoje${date ? ` — ${date}` : ''}`;
  return `Vence em ${days} ${days === 1 ? 'dia' : 'dias'}${date ? ` — ${date}` : ''}`;
}
